import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, MapPin, ArrowRight, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICES = [
  "Monthly Bookkeeping (Starter)",
  "Monthly Bookkeeping (Growth)",
  "Monthly Bookkeeping (Premium)",
  "Catch-Up / Clean-Up",
  "System Setup (QBO / Xero)",
  "Payroll Coordination",
  "Tax Services (Sole Prop)",
  "Year-End Package",
  "Other / Not Sure",
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    business_name: "",
    service_interest: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const onChange = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email, and a short message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message received. We'll respond within 24 hours.");
      setForm({
        name: "",
        email: "",
        phone: "",
        business_name: "",
        service_interest: "",
        message: "",
      });
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        err?.response?.data?.detail?.[0]?.msg ||
        "Something went wrong. Please try again or email us directly.";
      toast.error(typeof detail === "string" ? detail : "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      className="section-anchor py-24 md:py-32 bg-accent/50"
      data-testid="contact-section"
    >
      <div className="px-6 md:px-12 lg:px-20 max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="uppercase text-xs tracking-[0.25em] font-bold text-gold mb-4">
              Get in touch
            </div>
            <h2 className="font-serif text-4xl md:text-5xl text-navy tracking-tight leading-[1.05]">
              Book your free <span className="italic">consultation.</span>
            </h2>
            <p className="mt-6 text-stone text-base md:text-lg leading-relaxed max-w-md">
              Tell us a bit about your business and what you need. We&rsquo;ll respond
              within 24 hours with a tailored proposal — or simply a straight answer to
              your question.
            </p>

            <div className="mt-10 space-y-5">
              <ContactItem Icon={Mail} label="Email" value="info@dsobookssolutions.com" testId="contact-email" />
              <ContactItem Icon={Phone} label="Phone" value="(416) 833-9464" testId="contact-phone" />
              <ContactItem Icon={MapPin} label="Location" value="Ontario, Canada · Virtual" testId="contact-location" />
            </div>

            <div className="mt-10 border-l-2 border-gold pl-5 py-2">
              <div className="text-xs uppercase tracking-[0.2em] text-stone mb-1">
                Response promise
              </div>
              <div className="font-serif text-2xl text-navy">Within 24 hours, every weekday.</div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <form
              onSubmit={submit}
              data-testid="contact-form"
              className="bg-white border border-border p-8 md:p-10"
            >
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Full Name *">
                  <Input
                    data-testid="contact-input-name"
                    required
                    value={form.name}
                    onChange={(e) => onChange("name", e.target.value)}
                    placeholder="Jane Doe"
                    className="bg-cream/40 border-border focus-visible:ring-navy"
                  />
                </Field>
                <Field label="Email *">
                  <Input
                    data-testid="contact-input-email"
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => onChange("email", e.target.value)}
                    placeholder="jane@yourbusiness.ca"
                    className="bg-cream/40 border-border focus-visible:ring-navy"
                  />
                </Field>
                <Field label="Phone">
                  <Input
                    data-testid="contact-input-phone"
                    value={form.phone}
                    onChange={(e) => onChange("phone", e.target.value)}
                    placeholder="(416) 555-0123"
                    className="bg-cream/40 border-border focus-visible:ring-navy"
                  />
                </Field>
                <Field label="Business Name">
                  <Input
                    data-testid="contact-input-business"
                    value={form.business_name}
                    onChange={(e) => onChange("business_name", e.target.value)}
                    placeholder="Your Co. Ltd."
                    className="bg-cream/40 border-border focus-visible:ring-navy"
                  />
                </Field>
                <div className="sm:col-span-2">
                  <Field label="What can we help with?">
                    <Select
                      value={form.service_interest}
                      onValueChange={(v) => onChange("service_interest", v)}
                    >
                      <SelectTrigger
                        data-testid="contact-select-service"
                        className="bg-cream/40 border-border focus:ring-navy w-full"
                      >
                        <SelectValue placeholder="Select a service (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        {SERVICES.map((s) => (
                          <SelectItem key={s} value={s} data-testid={`service-option-${s}`}>
                            {s}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                </div>
                <div className="sm:col-span-2">
                  <Field label="Message *">
                    <Textarea
                      data-testid="contact-input-message"
                      required
                      rows={5}
                      value={form.message}
                      onChange={(e) => onChange("message", e.target.value)}
                      placeholder="Tell us about your business, monthly transaction volume, and what you need help with…"
                      className="bg-cream/40 border-border focus-visible:ring-navy resize-none"
                    />
                  </Field>
                </div>
              </div>

              <button
                type="submit"
                disabled={submitting}
                data-testid="contact-submit-button"
                className="mt-8 w-full sm:w-auto bg-navy text-cream px-8 py-4 inline-flex items-center justify-center gap-2 hover:bg-navy2 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Sending…
                  </>
                ) : (
                  <>
                    Send Message
                    <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </button>

              <p className="mt-4 text-xs text-stone">
                By submitting this form, you agree to be contacted about your enquiry.
                We never sell or share your data.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-[0.18em] text-stone block mb-2 font-medium">
        {label}
      </span>
      {children}
    </label>
  );
}

function ContactItem({ Icon, label, value, testId }) {
  return (
    <div className="flex items-start gap-4" data-testid={testId}>
      <div className="w-10 h-10 bg-navy text-cream inline-flex items-center justify-center shrink-0">
        <Icon className="h-4 w-4" />
      </div>
      <div>
        <div className="text-xs uppercase tracking-[0.18em] text-stone">{label}</div>
        <div className="text-ink font-medium mt-0.5">{value}</div>
      </div>
    </div>
  );
}
